import { SegmentsAudioPlaybackProvider } from '@app/core/playback/audio/SegmentsAudioPlaybackProvider';
import { useHasPlaybackContext } from '@app/core/playback/hooks/usePlayback';
import { DateTimeStr, toTimestamp } from '@mi-front/core';
import { DurationForRange } from '@mi-front/ui';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { LDate } from '../../../../../../../../components/date/LDate';
import { LTime } from '../../../../../../../../components/date/LTime';
import { DisplayableChunk } from '../../../../../../types';
import { ChunkActions } from './ChunkActions';
import { ChunkSetLabelButton } from './ChunkSetLabelButton';
import { ChunkStream } from './ChunkStream';
import { useChunkPlayback } from './useChunkPlaybackToggle';
import { Station } from '@app/core/types/stations';

export interface ChunkPlayerProps {
  /** The chunk to render */
  chunk: DisplayableChunk;
  /** Index of this chunk in the list */
  index: number;
  /** Total number of chunks (used for move button visibility) */
  totalChunks: number;
  /** Station for audio playback */
  station: Station;
  /** Whether to show edit controls (delete, move, label edit) */
  editable?: boolean;
  /** Whether to show the header (index, label, date/time) */
  showHeader?: boolean;
  /** Callbacks for edit actions (required when editable=true) */
  onDelete?: () => void;
  onMoveBefore?: () => void;
  onMoveAfter?: () => void;
  onSetLabel?: (label: string) => void;
}

/**
 * Unified chunk player component used in both fragment view and editor.
 * Automatically detects playback mode:
 * - If a PlaybackProvider exists in parent: uses parent's context (plays chunk bounds explicitly)
 * - Otherwise: creates its own PlaybackProvider (independent playback)
 * Supports both read-only (fragment view) and editable (editor) modes.
 */
export function ChunkPlayer({
  chunk,
  index,
  totalChunks,
  station,
  editable = false,
  showHeader = true,
  onDelete,
  onMoveBefore,
  onMoveAfter,
  onSetLabel,
}: ChunkPlayerProps) {
  const hasParentContext = useHasPlaybackContext();
  const { playChunk } = useChunkPlayback(chunk);

  const startTs = toTimestamp(chunk.from);
  const stopTs = toTimestamp(chunk.to);

  const isFirst = index === 0;
  const isLast = index === totalChunks - 1;

  // Render chunk label or placeholder
  const chunkLabel = chunk.label ? (
    <span>{chunk.label}</span>
  ) : (
    <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
      <FormattedMessage id="jointFragments.props.chunk" defaultMessage="Segmento" /> #{index + 1}
    </Box>
  );

  // Build header title
  const headerTitle = showHeader ? (
    <Typography variant="subtitle1" component="h6" sx={{ fontWeight: 600 }}>
      <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
        {index + 1}.
      </Box>
      {chunkLabel}
    </Typography>
  ) : undefined;

  // Build header actions (edit, delete, move buttons)
  const headerActions =
    showHeader && editable ? (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {onSetLabel && <ChunkSetLabelButton chunk={chunk} onSetLabel={onSetLabel} />}
        {onDelete && (
          <Tooltip
            title={
              <FormattedMessage
                id="jointFragments.editorScreen.chunkActions.delete"
                defaultMessage="Eliminar este segmento"
              />
            }
          >
            <IconButton size="small" onClick={onDelete} color="error" data-testid="delete-chunk">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!isFirst && onMoveBefore && (
          <Tooltip
            title={
              <FormattedMessage
                id="jointFragments.editorScreen.chunkActions.moveBefore"
                defaultMessage="Mover antes"
              />
            }
          >
            <IconButton size="small" onClick={onMoveBefore} data-testid="move-chunk-before">
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!isLast && onMoveAfter && (
          <Tooltip
            title={
              <FormattedMessage
                id="jointFragments.editorScreen.chunkActions.moveAfter"
                defaultMessage="Mover después"
              />
            }
          >
            <IconButton size="small" onClick={onMoveAfter} data-testid="move-chunk-after">
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ) : undefined;

  const content = (
    <Card sx={{ mb: 3 }}>
      {showHeader && <CardHeader title={headerTitle} />}
      <CardContent>
        {showHeader && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              gap: 1,
              fontSize: '0.875rem',
              color: 'text.secondary',
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: 'small' }} />
            <LDate value={chunk.from as DateTimeStr} day="2-digit" month="2-digit" />{' '}
            <LTime value={chunk.from as DateTimeStr} second={'2-digit'} />
            <span>-</span>
            <span>
              <DurationForRange from={chunk.from} to={chunk.to} />
            </span>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <ChunkActions onPlay={playChunk} />
          <ChunkStream chunk={chunk} />
        </Box>
      </CardContent>
      <CardActions>{headerActions}</CardActions>
    </Card>
  );

  // If parent context exists, use it (don't wrap with new provider)
  if (hasParentContext) {
    return content;
  }

  // No parent context: create own PlaybackProvider
  return (
    <SegmentsAudioPlaybackProvider station={station} playbackRange={{ from: startTs, to: stopTs }}>
      {content}
    </SegmentsAudioPlaybackProvider>
  );
}
